import type { ComponentProps, PropsWithChildren, RefObject } from 'react';
import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { FlexProps, OverflowProps, PositionProps } from 'styled-system';
import { overflow, position } from 'styled-system';

import theme from 'common/theme/theme';
import { useLocalization } from '../../providers/LocaleProvider';
import type { OverflowXProps } from '../../theme/system-utilities';
import { overflowX } from '../../theme/system-utilities';
import { Flex } from '../Flex';
import SwipeHandler from '../image-carousel/SwipeHandler';
import { HeadlineH7, SubtitleST4 } from '../Typography';
import type { SwipeDirection } from './constants';
import Controls from './controls';
import { useActiveIndex } from './hooks/useActiveIndex';
import { useCarousel } from './hooks/useCarousel';
import { useControls } from './hooks/useControls';

const Container = styled.div<PositionProps & OverflowProps & CarouselProps>`
  ${position};
  ${overflow};
  ${props => props.withBoxShadow && `margin-left: -${theme.space.sp_8}px;`}
`;

const containerStyles: ComponentProps<typeof Container> = {
  position: 'relative',
  overflow: 'hidden',
};

const Items = styled(Flex)<FlexProps & OverflowXProps>`
  ${overflowX};
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const getGapStyles = (slideGap: number | number[]) => {
  if (typeof slideGap === 'number') {
    return `${slideGap}px`;
  }

  return slideGap.map(gap => `${gap}px`);
};

const getItemsStyles = (slideGap: number | number[] = 0): ComponentProps<typeof Items> => ({
  flexWrap: 'nowrap',
  overflowX: 'auto',
  gap: getGapStyles(slideGap),
});

const SliderHeader = styled(Flex)``;

const sliderHeaderStyles: ComponentProps<typeof SliderHeader> = {
  alignItems: 'center',
  mb: 'sp_24',
};

export interface CarouselHandlers {
  swipe: (dir: SwipeDirection) => void;
  scrollToIndex: (scrollLeft: number) => void;
  isScrollable: (scroll: number, dir: SwipeDirection) => boolean;
}

export type HandlerRef = RefObject<Nullable<CarouselHandlers>>;

export type CarouselRef = RefObject<Nullable<HTMLDivElement>>;

export interface CarouselControlsProps {
  hasPrev: boolean;
  hasNext: boolean;
  getPrevIndex: (currentIndex: number) => number;
  getNextIndex: (currentIndex: number) => number;
}

export interface CarouselProps extends PropsWithChildren {
  scrollSlidesAmount?: number;
  afterSlide?: (arg: number) => void;
  slideGap?: number | number[];
  title?: Nullable<string>;
  titleId?: string;
  subtitle?: string;
  withTopControls?: boolean;
  titleContainerStyles?: ComponentProps<typeof Flex>;
  withBoxShadow?: boolean;
  renderControls?: (props: CarouselControlsProps) => React.ReactNode;
  activeIndex?: number;
  ariaProps?: {
    role?: string;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    'aria-roledescription'?: string;
  };
}

interface AccessibilityPropsParams {
  hasVisibleTitle: boolean;
  titleId: string | undefined;
  ariaProps?: CarouselProps['ariaProps'];
}

const useAccessibilityProps = ({
  hasVisibleTitle,
  titleId,
  ariaProps,
}: AccessibilityPropsParams) => {
  const { t } = useLocalization();

  const defaultProps = hasVisibleTitle
    ? {
        role: 'region' as const,
        'aria-labelledby': titleId,
        'aria-roledescription': t('h24_carousel'),
      }
    : {
        role: 'region' as const,
        'aria-label': t('h24_carousel'),
      };

  return { ...defaultProps, ...ariaProps };
};

const Carousel = ({
  children,
  scrollSlidesAmount,
  slideGap,
  afterSlide,
  title,
  titleId,
  subtitle,
  withTopControls,
  titleContainerStyles,
  withBoxShadow,
  renderControls,
  ariaProps,
  activeIndex = null,
}: CarouselProps) => {
  const carouselRef = useRef(null);
  const handlerRef = useRef(null);
  const [scroll, setScroll] = useState(0);

  const { handleScroll } = useCarousel({
    carouselRef,
    handlerRef,
    scrollSlidesAmount,
    afterSlide,
    setScroll,
  });
  const { swipePrev, swipeNext, hasPrev, hasNext } = useControls(handlerRef, scroll);
  const { getPrevIndex, getNextIndex } = useActiveIndex(carouselRef, handlerRef, activeIndex);

  const hasVisibleTitle = Boolean(withTopControls && title && titleId);
  const accessibilityProps = useAccessibilityProps({
    hasVisibleTitle,
    titleId,
    ariaProps,
  });

  return (
    <>
      {withTopControls && (
        <SliderHeader
          data-testid="carousel-header"
          {...sliderHeaderStyles}
          justifyContent={title ? 'space-between' : 'end'}
          width="100%"
        >
          <Flex flexDirection="column" {...titleContainerStyles}>
            {title && <HeadlineH7 id={titleId}>{title}</HeadlineH7>}
            {subtitle && <SubtitleST4 color="sys.neutral.text.default">{subtitle}</SubtitleST4>}
          </Flex>
          <Controls
            prevDisabled={!hasPrev}
            nextDisabled={!hasNext}
            handlePrevControl={swipePrev}
            handleNextControl={swipeNext}
          />
        </SliderHeader>
      )}
      <SwipeHandler onSwipeLeft={swipeNext} onSwipeRight={swipePrev}>
        <Container {...containerStyles} {...accessibilityProps} withBoxShadow={withBoxShadow}>
          <Items
            {...getItemsStyles(slideGap)}
            {...(withBoxShadow && { p: 'sp_8' })}
            ref={carouselRef}
            onScroll={handleScroll}
          >
            {children}
          </Items>
        </Container>
      </SwipeHandler>
      {renderControls?.({ hasPrev, hasNext, getPrevIndex, getNextIndex })}
    </>
  );
};

export default Carousel;
